import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import moment from "moment";
import Menu from "../menu/menu";
import QuizHistoryTable from "../../components/quiz-history-table/quiz-history-table";
import TaggedQuestionsSection from "./tagged-questions-section";
import GuidedPracticeSection from "./guided-practice-section";
import { SurgeryStudyResources } from "../../components/question-resources/question-resources";
import StudentRecentWork from "./student-recent-work";
import { QUESTION_SELECTIONS, FRESH_QUESTION_HELP } from "./student-practice-options";
import PracticeSuggestions from "./practice-suggestions";
import { api } from "../../services/apiService";
import { CONSTANTS } from "../../constants/app-constant";
import "./mentorships.scss";

// Slice D: debounce for the topic-search input. Deliberately under the
// 1000ms default waitFor timeout so the tests can run on real timers.
const TOPIC_SEARCH_DEBOUNCE_MS = 400;
// Same trigger length the live PowerSearchField autocomplete uses (the
// backend additionally rejects keywords under 2 chars with a 400).
const TOPIC_SEARCH_MIN_CHARS = CONSTANTS.powerSearchCharLength || 4;

/**
 * Phase-2 tutor view: a linked student's quiz progress (view-only). Reuses the
 * QuizHistoryTable in viewOnly mode. The read is server-side guarded by an
 * ACTIVE mentorship; the endpoint 404s gracefully until the backend lands.
 * Reached only via the flag-gated /mentorships/students/:id route.
 */
export default function MentorshipStudentPage() {
  const { studentUserId } = useParams();
  // named routerHistory: `history` below is the quiz-history data array
  const routerHistory = useHistory();
  // Same role read as mentorships.jsx. The server is the real guard, but without
  // this a student who pastes the URL sees a live "Assign a quiz" button whose
  // POST can only ever fail with a retry prompt. Two roles may view a student's
  // progress: a linked TUTOR (write actions available) and an ADMIN (read-only
  // oversight — no link required, no assign/tag surfaces).
  const role = typeof window !== "undefined" ? window.localStorage.getItem("role") : null;
  const isTutor = role === "ROLE_TUTOR";
  const isAdmin = role === "ROLE_ADMIN";
  const canView = isTutor || isAdmin;
  const [studentName, setStudentName] = useState("");
  const [quizHistory, setQuizHistory] = useState(null);
  // Slice E: "tutor-assigned only" filter for the history table. Closes the
  // assign->track loop — after a tutor assigns quizzes (Slice C/D), this lets
  // them isolate the ones they assigned and see how the student did. Keyed on
  // the same field the assigned badge already renders from (row.assignedByTutorId,
  // projected by the backend #448 into the same aggregation the student's own
  // history uses), so no backend change and no id-format assumption.
  const [assignedOnly, setAssignedOnly] = useState(false);
  // Start loading so the first paint shows the spinner, not a flash of the
  // empty state, before the mount effect fires.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Phase-3 (Slice C): tutor assigns a quiz with a real topic picker — exam +
  // count as before, plus optional discipline/organ-system filters and a name.
  const [assignOpen, setAssignOpen] = useState(false);
  const [examType, setExamType] = useState(CONSTANTS.examTypeList?.[0]?.value || "ABSITE");
  const [qCount, setQCount] = useState(10);
  const [questionSelection, setQuestionSelection] = useState("FRESH");
  const [quizName, setQuizName] = useState("");
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [selectedOrgSystems, setSelectedOrgSystems] = useState([]);
  // Category lists come from the same endpoint the create-quiz flow uses
  // (api.getCategories -> /qlearn/quiz/categories). Fetched lazily when the
  // dialog opens and retried on every open until both lists are in — the
  // payload needs the FULL lists (see onAssign), so while they're missing the
  // Assign button stays disabled instead of firing a POST known to 500.
  const [catLists, setCatLists] = useState({ discipline: [], organSystem: [] });
  const [catsLoading, setCatsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSuccess, setAssignSuccess] = useState("");
  // Slice D: "Search by topic" mode — the assign-dialog twin of the live
  // create-quiz power search. OFF (default) keeps the Slice C category picker
  // and its payload byte-identical; ON swaps the discipline/organ-system
  // selects for a debounced topic search whose picks become removable chips.
  const [topicMode, setTopicMode] = useState(false);
  const [topicQuery, setTopicQuery] = useState("");
  // null = no completed search yet (nothing to show); {topics, subtopics}
  // after a search resolves — either list may legitimately be empty.
  const [topicSuggestions, setTopicSuggestions] = useState(null);
  const [topicSearchLoading, setTopicSearchLoading] = useState(false);
  const [topicSearchFailed, setTopicSearchFailed] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);
  // Monotonic sequence so a slow earlier search can't clobber the results of
  // a later one (the debounce narrows but doesn't close that race).
  const topicSearchSeq = useRef(0);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError("");
    try {
      const res = await api.getMentorshipStudentQuizHistory(studentUserId);
      if (seq !== loadSeq.current) return;
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Backend not enabled yet — show an empty history, not an error.
        setQuizHistory([]);
        return;
      }
      if (Array.isArray(res)) {
        setQuizHistory(res);
      } else if (Array.isArray(res?.quizHistory)) {
        setQuizHistory(res.quizHistory);
        setStudentName(res.studentName || "");
      } else {
        setError("Couldn't load this student's progress. Please try again.");
        setQuizHistory(null);
      }
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError("Couldn't load this student's progress. Please try again.");
      setQuizHistory(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [studentUserId]);

  useEffect(() => {
    setQuizHistory(null);
    setStudentName("");
    setAssignOpen(false);
    setAssigning(false);
    setQuestionSelection("FRESH");
    setSelectedDisciplines([]);
    setSelectedOrgSystems([]);
    if (canView) load();
    return () => { loadSeq.current += 1; };
  }, [canView, load]);

  // Both lists are required to build a valid payload; treat a partial body
  // (one list missing) the same as a failed fetch.
  const catsReady = catLists.discipline.length > 0 && catLists.organSystem.length > 0;

  const loadCategories = useCallback(async () => {
    setCatsLoading(true);
    try {
      // doGetCall resolves a 200 to the body ({discipline, organSystem,
      // examType} arrays) and a non-200 to {status, errMsg}; a network reject
      // resolves to the Error. Anything non-array degrades to empty lists.
      const res = await api.getCategories();
      setCatLists({
        discipline: Array.isArray(res?.discipline) ? res.discipline : [],
        organSystem: Array.isArray(res?.organSystem) ? res.organSystem : [],
      });
    } catch (e) {
      setCatLists({ discipline: [], organSystem: [] });
    }
    setCatsLoading(false);
  }, []);

  const openAssign = () => {
    setAssignError("");
    setAssignSuccess("");
    setAssignOpen(true);
    // No once-only latch: while the lists are absent (first open, or every
    // previous fetch failed), retry on each open. Once both lists are in,
    // they're stable for the session — skip the refetch.
    if (!catsReady && !catsLoading) loadCategories();
  };

  // Slice D: debounced topic search against the same endpoint live power
  // search uses (api.searchTopics -> /medical-categorization/categories/v2/
  // search?keyword=). The effect's cleanup cancels the pending timer on every
  // keystroke, which is the debounce.
  useEffect(() => {
    if (!assignOpen || !topicMode) return undefined;
    const q = topicQuery.trim();
    if (q.length < TOPIC_SEARCH_MIN_CHARS) {
      setTopicSuggestions(null);
      setTopicSearchFailed(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      const seq = ++topicSearchSeq.current;
      setTopicSearchLoading(true);
      let next = null;
      let failed = false;
      try {
        // doGetCall resolves a 200 to the body ({topics, subtopics} string
        // arrays), a non-200 to {status, errMsg}, and a network reject to the
        // Error itself — it never throws. Anything without the arrays is a
        // failure: the search degrades to a hint, never blocks the dialog.
        const res = await api.searchTopics(encodeURIComponent(q));
        const ok = res && !(res instanceof Error) && !res.status &&
          (Array.isArray(res.topics) || Array.isArray(res.subtopics));
        if (ok) {
          next = {
            topics: Array.isArray(res.topics) ? res.topics : [],
            subtopics: Array.isArray(res.subtopics) ? res.subtopics : [],
          };
        } else {
          failed = true;
        }
      } catch (e) {
        failed = true;
      }
      if (seq !== topicSearchSeq.current) return; // a newer search superseded this one
      setTopicSuggestions(next);
      setTopicSearchFailed(failed);
      setTopicSearchLoading(false);
    }, TOPIC_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [assignOpen, topicMode, topicQuery]);

  // Switching modes drops the OTHER mode's selections (and the search box):
  // the payload can only ever be built from the picker that's on screen, so a
  // category-mode POST can never smuggle a stale medicalCategoriesMap in, and
  // a topic-mode POST can never re-filter the topic pool with stale
  // discipline/organ-system picks.
  const onToggleTopicMode = (e) => {
    setTopicMode(e.target.checked);
    setSelectedDisciplines([]);
    setSelectedOrgSystems([]);
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setTopicQuery("");
    setTopicSuggestions(null);
    setTopicSearchFailed(false);
    setTopicSearchLoading(false);
    topicSearchSeq.current += 1; // invalidate any in-flight search
  };

  const addTopicPick = (kind, value) => {
    const setter = kind === "topic" ? setSelectedTopics : setSelectedSubtopics;
    setter((prev) => (prev.includes(value) ? prev : [...prev, value]));
  };

  const removeTopicPick = (kind, value) => {
    const setter = kind === "topic" ? setSelectedTopics : setSelectedSubtopics;
    setter((prev) => prev.filter((v) => v !== value));
  };

  const onAssign = async () => {
    const seq = loadSeq.current;
    setAssigning(true);
    setAssignError("");
    let ok = false;
    // Slice D: the backend's own failure reason (e.g. QuizManager's
    // "insufficient questions" BadRequestException) when it sent one.
    let serverMsg = "";
    try {
      // doUpdateCall resolves {errStatus, errMsg} on failure (never rejects).
      const count = Math.min(100, Math.max(1, Math.round(Number(qCount)) || 10));
      let body;
      if (topicMode) {
        // Live power-search payload shape EXACTLY (create-quiz-page.jsx
        // onCreateQuiz, toggleSearchSwitch branch): examType-only categoryMap
        // + medicalCategoriesMap. Do NOT send the full discipline/organSystem
        // lists here — QuizManager deliberately skips the categoryMap
        // default-fill on the power-search branch (its selection query treats
        // missing dims as "no filter"), so full lists would re-filter the
        // medical-topic pool. categoryMap itself must still be present:
        // MentorshipManager.assignQuiz 400s on a null/empty map.
        body = {
          noOfQuestions: count,
          categoryMap: { examType: [examType] },
          medicalCategoriesMap: {
            topics: [...selectedTopics],
            subtopics: [...selectedSubtopics],
          },
        };
      } else {
        // Match the live create-quiz semantics (getCategoryMap in
        // create-quiz-page.jsx): ALWAYS send full arrays — "whole exam" is
        // expressed as the full list for a dimension, never an omitted key or
        // an empty array. QuizManager.createQuizRandomized queries Mongo with
        // categoryMap values BEFORE its default-fill block, so an omitted key
        // reaches Criteria.in(null) -> "$in needs an array" -> server 500, and
        // $in: [] matches zero questions. onAssign is only reachable with
        // catsReady (the submit button is disabled otherwise). No
        // medicalCategoriesMap key AT ALL in this mode — its mere presence
        // flips QuizManager onto the power-search branch.
        body = {
          noOfQuestions: count,
          categoryMap: {
            examType: [examType],
            discipline: selectedDisciplines.length ? [...selectedDisciplines] : [...catLists.discipline],
            organSystem: selectedOrgSystems.length ? [...selectedOrgSystems] : [...catLists.organSystem],
          },
        };
      }
      body.questionSelection = questionSelection;
      const trimmedName = quizName.trim();
      if (trimmedName) body.name = trimmedName; // server defaults the name when omitted
      const res = await api.assignMentorshipQuiz(studentUserId, body);
      if (seq !== loadSeq.current) return;
      ok = !(res?.errStatus || res?.errMsg);
      if (ok) {
        // Consume the created-quiz body (contract-pinned): name the quiz in the
        // confirmation so the tutor can find it in the student's history.
        const createdName = typeof res?.name === "string" && res.name.trim() ? ` “${res.name.trim()}”` : "";
        setAssignSuccess(`Quiz${createdName} assigned — it will appear in the student's history.`);
        // Reset the per-assignment fields so the next open starts fresh
        // (prevents accidentally re-assigning the previous topics/name).
        setSelectedDisciplines([]);
        setSelectedOrgSystems([]);
        setSelectedTopics([]);
        setSelectedSubtopics([]);
        setTopicQuery("");
        setTopicSuggestions(null);
        setQuizName("");
      } else {
        // errMsg carries the parsed error body: for backend exceptions an
        // ErrorResponse object ({status, message}), for a network reject the
        // fixed errorMsgs[500] string. Prefer the server's own words (e.g.
        // "insufficient questions… select fewer") over the generic fallback.
        const raw = res?.errMsg;
        const msg = typeof raw === "string" ? raw : raw && typeof raw.message === "string" ? raw.message : "";
        serverMsg = msg.trim();
      }
    } catch (e) {
      ok = false;
    }
    if (seq !== loadSeq.current) return;
    setAssigning(false);
    if (ok) {
      setAssignOpen(false);
      load();
    } else {
      setAssignError(serverMsg || "Couldn't assign the quiz. Please try again.");
    }
  };

  if (!canView) {
    return (
      <Box className="body-cont-over mentorships-page">
        <Menu />
        <div className="mtr-progress">
          <Link to="/mentorships" className="mtr-back">
            <ArrowBack fontSize="small" /> Back to mentorships
          </Link>
          <Typography variant="body2" className="mtr-sub" data-testid="mtr-not-tutor">
            Student progress is available to tutors only.
          </Typography>
        </div>
      </Box>
    );
  }

  // Slice E: a row is "tutor-assigned" when assignedByTutorId is present — the
  // same truthiness the in-table badge uses. `effectiveAssignedOnly` self-heals:
  // if a reload leaves zero assigned rows the toggle disappears, so we drop the
  // filter too rather than stranding the tutor on an empty filtered table.
  const history = quizHistory || [];
  const assignedCount = history.filter((q) => q && q.assignedByTutorId).length;
  const effectiveAssignedOnly = assignedOnly && assignedCount > 0;
  const shownHistory = effectiveAssignedOnly
    ? history.filter((q) => q && q.assignedByTutorId)
    : history;

  // Slice F: at-a-glance progress summary over the currently-shown set, so it
  // recomputes when the Slice E "tutor-assigned only" filter toggles. Client-
  // side over already-fetched rows — no backend change, no new call.
  const shownCount = shownHistory.length;
  // Average the scores of COMPLETED quizzes only. percentageCorrect is a
  // primitive double that serializes 0 for an unfinished quiz, so gating on the
  // completion flag keeps a half-finished 0 from dragging the average down.
  // Field contract (verified against UserQuizHistoryDto @JsonProperty on
  // QLearn-service main): the boolean completion flag serializes as `iscomplete`,
  // and the completion timestamp (LocalDateTime completeDate) as `complete` — the
  // same `complete` the quiz-history table already renders with moment().
  const scoredRows = shownHistory.filter(
    (q) => q && q.iscomplete && Number.isFinite(q.percentageCorrect)
  );
  const avgScore = scoredRows.length
    ? Math.round(
        scoredRows.reduce((sum, q) => sum + q.percentageCorrect, 0) / scoredRows.length
      )
    : null;
  // "Last active" = the most recent completion timestamp among shown quizzes,
  // reusing the exact moment(row.complete) contract the history table renders.
  const completionMoments = shownHistory
    .map((q) => q && q.complete)
    .filter((c) => c && moment(c).isValid())
    .map((c) => moment(c));
  const lastActive = completionMoments.length
    ? moment.max(completionMoments).format("ll")
    : null;

  const openStudentQuiz = CONSTANTS.oversightQuizOpenEnabled
    ? (row) => routerHistory.push({
        pathname: `/mentorships/students/${studentUserId}/result/${row.id}`,
        state: { studentName },
      })
    : undefined;

  return (
    <Box className="body-cont-over mentorships-page">
      <Menu />
      <div className="mtr-progress">
        <Link to={isTutor ? { pathname: "/mentorships", state: routerHistory.location.state?.mentorshipReturnState } : "/mentorships"} className="mtr-back">
          <ArrowBack fontSize="small" /> {isAdmin ? "Back to all students" : "Back to my students"}
        </Link>
        <div className="mtr-header">
          <Typography variant="h5">
            {studentName ? `${studentName} — progress` : "Student progress"}
          </Typography>
          <Typography variant="body2" className="mtr-sub">
            {isAdmin
              ? "Admin view — read-only. You can see every student's quiz history for oversight."
              : "View-only access through your tutoring connection. Quiz answers and scores cannot be changed here."}
          </Typography>
          {isTutor && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              className="mtr-assign-btn"
              data-testid="mtr-assign-open"
              onClick={openAssign}
            >
              Assign a quiz
            </Button>
          )}
        </div>

        {assignSuccess && (
          <Typography className="mtr-success" variant="body2" data-testid="mtr-assign-success">
            {assignSuccess}
          </Typography>
        )}

        {error && (
          <Typography className="mtr-error" variant="body2" data-testid="mtr-error">
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <StudentRecentWork quizzes={history} onReview={openStudentQuiz} />
        )}

        {/* Tutor-led practice: answers stay separate from student scores;
            explicit coverage saves remember questions for this student. `key`
            remounts it per student so one student's questions/reveals can
            never carry into another's page. The server re-checks the link. */}
        {isTutor && CONSTANTS.guidedPracticeEnabled && (
          <GuidedPracticeSection
            key={studentUserId}
            studentUserId={studentUserId}
            studentName={studentName}
            quizHistory={loading || error ? null : history}
          />
        )}

        {/* T4: tags this student addressed to the caller, above the history
            table per §10.4. Tutor-only via the page gate above; sub-flag-gated
            like the T3 section (route mounts only under mentorshipsEnabled, so
            both flags are required); renders nothing when the student has no
            tags or the backend is dark. */}
        {isTutor && CONSTANTS.mentorshipTagsEnabled && (
          <TaggedQuestionsSection studentUserId={studentUserId} studentName={studentName} />
        )}

        {/* Slice F: at-a-glance progress summary above the filter so it frames
            the whole set. Computed over shownHistory, so it reflects the Slice E
            filter; hidden while loading, on error, or on an empty history. */}
        {!loading && !error && shownCount > 0 && (
          <Typography
            variant="body2"
            className="mtr-progress-summary"
            data-testid="mtr-progress-summary"
          >
            {shownCount === 1 ? "1 quiz" : `${shownCount} quizzes`}
            {avgScore !== null && ` · avg ${avgScore}%`}
            {lastActive && ` · last active ${lastActive}`}
          </Typography>
        )}

        {/* Slice E: the assigned-only filter appears only once there's at least
            one tutor-assigned quiz to isolate — otherwise it's noise. */}
        {!loading && !error && assignedCount > 0 && (
          <div className="mtr-assigned-filter" data-testid="mtr-assigned-filter">
            <FormControlLabel
              className="mtr-assigned-filter-label"
              control={
                <Switch
                  checked={effectiveAssignedOnly}
                  onChange={(e) => setAssignedOnly(e.target.checked)}
                  color="primary"
                  size="small"
                  inputProps={{
                    "data-testid": "mtr-assigned-filter-toggle",
                    "aria-label": "Show only tutor-assigned quizzes",
                  }}
                />
              }
              label={`Show tutor-assigned only (${assignedCount})`}
            />
          </div>
        )}

        {loading && !quizHistory ? (
          <div className="mtr-loading" data-testid="mtr-loading">
            <CircularProgress size={22} />
            <Typography variant="caption" className="mtr-sub">Loading…</Typography>
          </div>
        ) : error ? null : (
          // On error, only the banner above shows — never the empty table, which
          // would falsely claim the student has taken no quizzes.
          <QuizHistoryTable
            questions={shownHistory}
            isLoading={loading}
            viewOnly
            // Oversight quiz-open (contract qlearn-oversight-quiz-open-v1):
            // rows open the student's quiz through the guarded oversight
            // route. Server is the gate (403/404); this is navigation only.
            // Dark until the sub-flag ships with the backend flip (gate F4).
            onOversightOpen={openStudentQuiz}
          />
        )}
      </div>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} className="mtr-assign-dialog">
        <DialogTitle>Assign a quiz{studentName ? ` to ${studentName}` : ""}</DialogTitle>
        <DialogContent>
          <div className="mtr-assign-fields">
            <TextField
              select
              label="Exam"
              size="small"
              variant="outlined"
              value={examType}
              onChange={(e) => setExamType(e.target.value)}
              inputProps={{ "data-testid": "mtr-assign-exam" }}
            >
              {(CONSTANTS.examTypeList || []).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Questions" size="small" variant="outlined"
              value={questionSelection} onChange={e => setQuestionSelection(e.target.value)}
              helperText={questionSelection === "FRESH" ? FRESH_QUESTION_HELP : "Previous questions are included intentionally."}
              inputProps={{ "data-testid": "mtr-question-selection" }}>
              {QUESTION_SELECTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            {!loading && !error && catsReady && (
              <PracticeSuggestions history={history} exam={examType} catalog={catLists.discipline}
                questionSelection={questionSelection}
                onUseTopics={(topics) => {
                  setTopicMode(false);
                  setSelectedDisciplines(topics);
                  setSelectedOrgSystems([]);
                }}
                onReviewMissed={() => setQuestionSelection("INCORRECT")} />
            )}
            <TextField
              label="Number of questions"
              type="number"
              size="small"
              variant="outlined"
              value={qCount}
              onChange={(e) => setQCount(e.target.value)}
              inputProps={{ min: 1, max: 100, "data-testid": "mtr-assign-count" }}
            />
            <FormControlLabel
              className="mtr-topic-toggle"
              control={
                <Switch
                  checked={topicMode}
                  onChange={onToggleTopicMode}
                  color="primary"
                  size="small"
                  inputProps={{ "data-testid": "mtr-assign-topic-toggle", "aria-label": "Search by topic" }}
                />
              }
              label="Search by topic"
            />
            {topicMode && (
              <>
                <TextField
                  label="Search topics"
                  size="small"
                  variant="outlined"
                  placeholder={`Type at least ${TOPIC_SEARCH_MIN_CHARS} characters…`}
                  value={topicQuery}
                  onChange={(e) => setTopicQuery(e.target.value)}
                  inputProps={{ "data-testid": "mtr-assign-topic-search" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {topicSearchLoading && <CircularProgress size={18} />}
                      </InputAdornment>
                    ),
                  }}
                />
                {topicSearchFailed && (
                  <Typography variant="caption" className="mtr-dialog-error" data-testid="mtr-assign-topic-error">
                    Topic search isn&apos;t available right now — try again, or switch the toggle off to
                    filter by category instead.
                  </Typography>
                )}
                {topicSuggestions && (
                  <Paper
                    className="mtr-topic-suggestions"
                    elevation={0}
                    data-testid="mtr-assign-topic-suggestions"
                  >
                    {topicSuggestions.topics.length > 0 && (
                      <List dense disablePadding aria-label="Matching topics">
                        <ListItem className="mtr-topic-suggestion-header">
                          <ListItemText primary="Topics:" />
                        </ListItem>
                        {topicSuggestions.topics.map((t) => (
                          <ListItem button key={`t-${t}`} onClick={() => addTopicPick("topic", t)}>
                            <ListItemText primary={t} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    {topicSuggestions.subtopics.length > 0 && (
                      <List dense disablePadding aria-label="Matching subtopics">
                        <ListItem className="mtr-topic-suggestion-header">
                          <ListItemText primary="Subtopics:" />
                        </ListItem>
                        {topicSuggestions.subtopics.map((s) => (
                          <ListItem button key={`s-${s}`} onClick={() => addTopicPick("subtopic", s)}>
                            <ListItemText primary={s} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    {!topicSuggestions.topics.length && !topicSuggestions.subtopics.length && (
                      <List dense disablePadding>
                        <ListItem>
                          <ListItemText primary="No matching topics." />
                        </ListItem>
                      </List>
                    )}
                  </Paper>
                )}
                {(selectedTopics.length > 0 || selectedSubtopics.length > 0) && (
                  <div className="mtr-topic-chips" data-testid="mtr-assign-topic-chips">
                    {selectedTopics.map((t) => (
                      <Chip
                        key={`t-${t}`}
                        label={t}
                        size="small"
                        color="primary"
                        onDelete={() => removeTopicPick("topic", t)}
                        data-testid="mtr-assign-topic-chip"
                      />
                    ))}
                    {selectedSubtopics.map((s) => (
                      <Chip
                        key={`s-${s}`}
                        label={s}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onDelete={() => removeTopicPick("subtopic", s)}
                        data-testid="mtr-assign-topic-chip"
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            {!topicMode && catLists.discipline.length > 0 && (
              <TextField
                select
                label="Disciplines"
                size="small"
                variant="outlined"
                value={selectedDisciplines}
                onChange={(e) => setSelectedDisciplines(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (sel) => (sel && sel.length ? sel.join(", ") : "All disciplines"),
                  SelectDisplayProps: {
                    "data-testid": "mtr-assign-discipline",
                    "aria-label": "Disciplines",
                  },
                }}
              >
                {catLists.discipline.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </TextField>
            )}
            {!topicMode && catLists.organSystem.length > 0 && (
              <TextField
                select
                label="Organ systems"
                size="small"
                variant="outlined"
                value={selectedOrgSystems}
                onChange={(e) => setSelectedOrgSystems(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (sel) => (sel && sel.length ? sel.join(", ") : "All organ systems"),
                  SelectDisplayProps: {
                    "data-testid": "mtr-assign-orgsystem",
                    "aria-label": "Organ systems",
                  },
                }}
              >
                {catLists.organSystem.map((o) => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Quiz name (optional)"
              size="small"
              variant="outlined"
              value={quizName}
              onChange={(e) => setQuizName(e.target.value)}
              inputProps={{ maxLength: 120, "data-testid": "mtr-assign-name" }}
            />
            {examType === "ABSITE" && <SurgeryStudyResources />}
            {!topicMode && catsReady && (
              <Typography variant="caption" className="mtr-dialog-hint" data-testid="mtr-assign-hint">
                Topic filters are optional — leave them blank to draw questions from the whole exam.
              </Typography>
            )}
            {!topicMode && !catsReady && !catsLoading && (
              <Typography variant="caption" className="mtr-dialog-error" data-testid="mtr-assign-cats-error">
                Couldn&apos;t load exam topics — close and reopen this dialog to retry.
              </Typography>
            )}
            {topicMode && selectedTopics.length + selectedSubtopics.length === 0 && (
              <Typography variant="caption" className="mtr-dialog-hint" data-testid="mtr-assign-topic-hint">
                Search and pick at least one topic or subtopic to enable Assign.
              </Typography>
            )}
          </div>
          {assignError && (
            <Typography className="mtr-dialog-error" variant="body2" data-testid="mtr-assign-error">
              {assignError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onAssign}
            // Topic mode needs >=1 pick (an empty search would resolve a
            // zero/undefined power-search pool) but NOT the category lists —
            // its payload never uses them, so a failed getCategories doesn't
            // block topic-mode assigning. Category mode needs the full lists.
            disabled={
              assigning ||
              !Number(qCount) ||
              (topicMode ? selectedTopics.length + selectedSubtopics.length === 0 : !catsReady)
            }
            data-testid="mtr-assign-submit"
          >
            {assigning ? <CircularProgress size={18} color="inherit" /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
